import { useState, useEffect } from "react";
import { docsClient, authClient, Document, ExportInsert } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Terminal, Download } from "lucide-react";

interface CreateExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExportCreated: () => void;
}

export default function CreateExportDialog({
  open,
  onOpenChange,
  onExportCreated,
}: CreateExportDialogProps) {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [format, setFormat] = useState<"toc" | "claude" | "json">("claude");
  const [documents, setDocuments] = useState<Document[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagSearch, setTagSearch] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      loadDocuments();
    }
  }, [open]);

  const loadDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from("documents")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      const docs = (data || []) as Document[];
      setDocuments(docs);

      // Extract unique tags
      const tagsSet = new Set<string>();
      docs.forEach((doc) => {
        if (doc.tags && Array.isArray(doc.tags)) {
          doc.tags.forEach((tag) => tagsSet.add(tag));
        }
      });
      setAvailableTags(Array.from(tagsSet).sort());
    } catch (error) {
      console.error("Error loading documents:", error);
    }
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const getFilteredDocumentCount = () => {
    if (selectedTags.length === 0) {
      return documents.length;
    }
    return documents.filter((doc) => {
      if (!doc.tags || !Array.isArray(doc.tags)) return false;
      return selectedTags.some((tag) => doc.tags.includes(tag));
    }).length;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get user from authClient (Agency-Auth SSO)
      const {
        data: { user },
      } = await authClient.auth.getUser();

      if (!user) throw new Error("Not authenticated");

      const documentCount = getFilteredDocumentCount();

      // Create export record
      const { error } = await docsClient.from("exports").insert({
        user_id: user.id,
        name,
        format,
        tags: selectedTags.length > 0 ? selectedTags : null,
        document_count: documentCount,
        status: "pending",
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: `Export "${name}" created. Processing ${documentCount} document(s)...`,
      });

      // Reset form
      setName("");
      setFormat("claude");
      setSelectedTags([]);
      onOpenChange(false);
      onExportCreated();
    } catch (error) {
      console.error("Error creating export:", error);
      toast({
        title: "Error",
        description: "Failed to create export",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[650px]">
        <DialogHeader>
          <DialogTitle>Export Documentation</DialogTitle>
          <DialogDescription>Choose how you want to export your documentation</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="cli" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="cli" className="gap-2">
              <Terminal className="h-4 w-4" />
              CLI Export (Recommended)
            </TabsTrigger>
            <TabsTrigger value="web" className="gap-2">
              <Download className="h-4 w-4" />
              Web Download
            </TabsTrigger>
          </TabsList>

          {/* CLI Export Tab */}
          <TabsContent value="cli" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div className="rounded-lg bg-primary/5 border-2 border-primary/20 p-4 space-y-4">
                <div>
                  <h4 className="font-semibold text-lg mb-2">Export to Your Project</h4>
                  <p className="text-sm text-muted-foreground">
                    Select what documentation you need, then copy the command to run in your
                    project:
                  </p>
                </div>

                {/* Tag Selection */}
                <div className="space-y-3">
                  <div>
                    <Label className="text-base font-semibold">
                      What documentation do you need?
                    </Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Select tags below to filter what gets exported:
                    </p>
                  </div>

                  {/* Search box */}
                  <Input
                    placeholder="Search tags (e.g., react, mui, docker, typescript...)"
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    className="w-full"
                  />

                  {/* Tag list */}
                  <div className="max-h-48 overflow-y-auto border rounded-md p-3 space-y-2 bg-background">
                    {availableTags.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No tags available</p>
                    ) : (
                      availableTags
                        .filter((tag) => tag.toLowerCase().includes(tagSearch.toLowerCase()))
                        .map((tag) => (
                          <div key={tag} className="flex items-center space-x-2">
                            <Checkbox
                              id={`cli-tag-${tag}`}
                              checked={selectedTags.includes(tag)}
                              onCheckedChange={() => toggleTag(tag)}
                            />
                            <Label
                              htmlFor={`cli-tag-${tag}`}
                              className="font-normal cursor-pointer flex-1"
                            >
                              {tag}
                            </Label>
                          </div>
                        ))
                    )}
                    {tagSearch &&
                      availableTags.filter((tag) =>
                        tag.toLowerCase().includes(tagSearch.toLowerCase())
                      ).length === 0 && (
                        <p className="text-sm text-muted-foreground">No tags match "{tagSearch}"</p>
                      )}
                  </div>

                  {/* Selected tags display */}
                  {selectedTags.length > 0 && (
                    <div className="flex flex-wrap gap-2 p-3 bg-muted rounded-md">
                      <span className="text-xs font-semibold">Selected:</span>
                      {selectedTags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 bg-primary text-primary-foreground px-2 py-1 rounded text-xs cursor-pointer hover:bg-primary/80"
                          onClick={() => toggleTag(tag)}
                        >
                          {tag}
                          <span className="text-xs">×</span>
                        </span>
                      ))}
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground">
                    {selectedTags.length === 0
                      ? `📦 Will export all ${documents.length} documents`
                      : `📦 Will export ${getFilteredDocumentCount()} documents with selected tags`}
                  </p>
                </div>

                {/* Command Display */}
                <div className="space-y-3">
                  <div className="bg-slate-950 rounded-lg p-4 border-2 border-primary/40">
                    <div className="flex items-start justify-between gap-3">
                      <code className="text-sm font-mono text-green-400 flex-1 break-all">
                        npx localdocs-export export
                        {selectedTags.length > 0 && ` --tags ${selectedTags.join(",")}`}
                      </code>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="shrink-0"
                        onClick={() => {
                          const cmd = `npx localdocs-export export${selectedTags.length > 0 ? ` --tags ${selectedTags.join(",")}` : ""}`;
                          navigator.clipboard.writeText(cmd);
                          toast({
                            title: "Copied!",
                            description: "Command copied to clipboard",
                          });
                        }}
                      >
                        Copy Command
                      </Button>
                    </div>
                  </div>

                  {/* What it does */}
                  <div className="space-y-2 text-sm">
                    <p className="font-semibold">This command will:</p>
                    <div className="space-y-1 text-muted-foreground">
                      <div className="flex items-start gap-2">
                        <span className="text-green-600 shrink-0">✓</span>
                        <span>
                          Download {getFilteredDocumentCount()} documents to{" "}
                          <code className="bg-muted px-1 py-0.5 rounded text-xs">
                            docs/localdocs/
                          </code>
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-green-600 shrink-0">✓</span>
                        <span>
                          Create individual .md files (e.g.,{" "}
                          <code className="bg-muted px-1 py-0.5 rounded text-xs">a1b2c3d4.md</code>)
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-green-600 shrink-0">✓</span>
                        <span>
                          Generate{" "}
                          <code className="bg-muted px-1 py-0.5 rounded text-xs">
                            claude-refs.md
                          </code>{" "}
                          index with @file references
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-green-600 shrink-0">✓</span>
                        <span>Ready for Claude Code immediately</span>
                      </div>
                    </div>
                  </div>

                  {/* Custom output directory hint */}
                  <div className="p-3 bg-muted rounded-md text-xs text-muted-foreground">
                    <strong>Custom directory:</strong> Add{" "}
                    <code className="bg-background px-1 py-0.5 rounded mx-1">
                      --output docs/custom
                    </code>{" "}
                    to change destination
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Web Download Tab */}
          <TabsContent value="web" className="space-y-4 mt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="export-name">
                  Export Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="export-name"
                  placeholder="e.g., WordPress Documentation"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={loading}
                />
                <p className="text-xs text-muted-foreground">
                  Name this export for easy identification later
                </p>
              </div>

              <div className="space-y-2">
                <Label>
                  Export Format <span className="text-destructive">*</span>
                </Label>
                <RadioGroup
                  value={format}
                  onValueChange={(v: string) => setFormat(v as "toc" | "claude" | "json")}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="claude" id="format-claude" />
                    <Label htmlFor="format-claude" className="font-normal cursor-pointer">
                      <div>
                        <div className="font-medium">Claude (@file references)</div>
                        <div className="text-sm text-muted-foreground">
                          Create @file references for Claude Code
                        </div>
                      </div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="toc" id="format-toc" />
                    <Label htmlFor="format-toc" className="font-normal cursor-pointer">
                      <div>
                        <div className="font-medium">Table of Contents</div>
                        <div className="text-sm text-muted-foreground">
                          Index with links to all documents
                        </div>
                      </div>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="json" id="format-json" />
                    <Label htmlFor="format-json" className="font-normal cursor-pointer">
                      <div>
                        <div className="font-medium">JSON</div>
                        <div className="text-sm text-muted-foreground">
                          Self-contained JSON with all content
                        </div>
                      </div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label>Filter by Tags (optional)</Label>
                <div className="max-h-48 overflow-y-auto border rounded-md p-3 space-y-2">
                  {availableTags.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No tags available</p>
                  ) : (
                    availableTags.map((tag) => (
                      <div key={tag} className="flex items-center space-x-2">
                        <Checkbox
                          id={`web-tag-${tag}`}
                          checked={selectedTags.includes(tag)}
                          onCheckedChange={() => toggleTag(tag)}
                        />
                        <Label htmlFor={`web-tag-${tag}`} className="font-normal cursor-pointer">
                          {tag}
                        </Label>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {selectedTags.length === 0
                    ? `All ${documents.length} documents will be included`
                    : `${getFilteredDocumentCount()} document(s) match selected tags`}
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? "Creating..." : "Create Export"}
                </Button>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
